import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import type { ReevaluateAPInvoiceExceptionsInput } from '@erp/rpc';
import { reevaluateToast } from './reevaluateToast';

export { summarizeReevaluation } from './reevaluateToast';

/**
 * Re-run the exception evaluation for invoices (no re-matching, no email
 * lineage needed). Invalidates invoice queries so status / owner refresh.
 */
export function useReevaluateAPInvoiceExceptions(options?: {
  /** When false, the caller shows its own result UI (slide-in dialog). */
  notify?: boolean;
}) {
  const notify = options?.notify ?? true;
  const gt = useGT();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (
      input:
        | string[]
        | (Pick<ReevaluateAPInvoiceExceptionsInput, 'invoiceIds'> &
            Partial<
              Pick<ReevaluateAPInvoiceExceptionsInput, 'exceptionRuleSetId'>
            >),
    ) => {
      const payload = Array.isArray(input) ? { invoiceIds: input } : input;
      return rpcClient.accountsPayable.invoices.reevaluateExceptions(payload);
    },
    onSuccess: ({ results }) => {
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsPayable.invoices.key(),
      });
      if (!notify) return;
      showToast(reevaluateToast(gt, results));
    },
    onError: (error) => {
      if (!notify) return;
      showToast({
        title: gt('Re-evaluate failed', {
          context: 'toast title when the re-evaluate request itself fails',
        }),
        message: error instanceof Error ? error.message : String(error),
        color: 'error',
        icon: 'error',
      });
    },
  });
}
